import { useState, useRef, useEffect } from 'react';
import { Upload, Search, Grid3x3, List, MoreVertical, AlertTriangle, Image, Video, Music, FileJson, Box, Archive as ArchiveIcon, File, Folder, Edit3, X, Baseline, Trash2, Link } from 'lucide-react';
import assetService, { Asset } from '../../services/assetService';
import projectDataService from '../../services/projectDataService';
import { FILL_GRADIENT } from '../../constants/gradients';
import { analyticsService } from '../../services/analyticsService';

type AssetCategory = 'all' | 'images' | 'video' | 'audio' | 'fonts' | 'data' | '3d' | 'archive';

const CATEGORIES = [
  { id: 'all' as AssetCategory, label: 'All' },
  { id: 'images' as AssetCategory, label: 'Images' },
  { id: 'video' as AssetCategory, label: 'Video' },
  { id: 'audio' as AssetCategory, label: 'Audio' },
  { id: 'fonts' as AssetCategory, label: 'Fonts' },
  { id: 'data' as AssetCategory, label: 'Data' },
  { id: '3d' as AssetCategory, label: '3D Models' },
  { id: 'archive' as AssetCategory, label: 'Archive' },
];

// Get icon component based on file type and filename
function getFileTypeIcon(type: string, size: number = 20, filename: string = '') {
  const iconProps = { size, className: 'text-gray-500 dark:text-gray-400' };
  
  // Check file extension for better detection
  const ext = filename.toLowerCase().split('.').pop() || '';
  
  if (type.startsWith('image/')) {
    return <Image {...iconProps} />;
  } else if (type.startsWith('video/')) {
    return <Video {...iconProps} />;
  } else if (type.startsWith('audio/')) {
    return <Music {...iconProps} />;
  } else if (type.includes('font') || type.includes('ttf') || type.includes('woff') || type.includes('otf') || ['ttf', 'otf', 'woff', 'woff2', 'eot'].includes(ext)) {
    return <Baseline {...iconProps} />;
  } else if (type.includes('json') || type.includes('xml')) {
    return <FileJson {...iconProps} />;
  } else if (type.includes('zip') || type.includes('rar') || type.includes('archive')) {
    return <ArchiveIcon {...iconProps} />;
  } else if (type.includes('model') || type.includes('3d') || type.includes('obj') || type.includes('fbx') || ['obj', 'fbx', 'gltf', 'glb', 'dae', 'stl', 'ply', 'blend'].includes(ext)) {
    return <Box {...iconProps} />;
  } else {
    return <File {...iconProps} />;
  }
}

// Get icon component for category
function getCategoryIcon(category: AssetCategory, size: number = 16) {
  const iconProps = { size, className: 'flex-shrink-0' };
  
  switch (category) {
    case 'all':
      return <Folder {...iconProps} />;
    case 'images':
      return <Image {...iconProps} />;
    case 'video':
      return <Video {...iconProps} />;
    case 'audio':
      return <Music {...iconProps} />;
    case 'fonts':
      return <Baseline {...iconProps} />;
    case 'data':
      return <FileJson {...iconProps} />;
    case '3d':
      return <Box {...iconProps} />;
    case 'archive':
      return <ArchiveIcon {...iconProps} />;
    default:
      return <Folder {...iconProps} />;
  }
}

// Upload Progress Overlay Component
function UploadProgressOverlay({ progress, size = 'large' }: { progress: number; size?: 'small' | 'large' }) {
  // Adjust sizes based on view
  const svgSize = size === 'small' ? 40 : 120;
  const radius = size === 'small' ? 16 : 52;
  const strokeWidth = size === 'small' ? 3 : 5;
  const textSize = size === 'small' ? 'text-xs' : 'text-2xl';
  const center = svgSize / 2;
  
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20">
      <div className="relative">
        {/* Background Circle */}
        <svg width={svgSize} height={svgSize} className="transform -rotate-90">
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress Circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#a855f7"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-300"
          />
        </svg>
        {/* Percentage Text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-white font-semibold ${textSize}`}>
            {Math.round(progress)}%
          </span>
        </div>
      </div>
    </div>
  );
}

interface AssetBrowserProps {
  projectId: string;
  onSectionComplete?: () => void;
  droppedFiles?: File[];
  onFilesProcessed?: () => void;
  onAddToPrompt?: (assetId: string, assetName: string) => void;
  currentCode?: string;
}

export default function AssetBrowser({ projectId, onSectionComplete, droppedFiles, onFilesProcessed, onAddToPrompt, currentCode }: AssetBrowserProps) {
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalAssetCount, setTotalAssetCount] = useState(0);
  const [totalUnusedCount, setTotalUnusedCount] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState<Record<AssetCategory, number>>({
    all: 0,
    images: 0,
    video: 0,
    audio: 0,
    fonts: 0,
    data: 0,
    '3d': 0,
    archive: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState<Asset | null>(null);
  const [hoveredAssetId, setHoveredAssetId] = useState<string | null>(null);
  const [deleteConfirmAsset, setDeleteConfirmAsset] = useState<Asset | null>(null);
  const [deleteConfirmAssets, setDeleteConfirmAssets] = useState<Asset[]>([]);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processedFilesRef = useRef<File[] | null>(null);

  const isSelectMode = selectedAssets.length > 0;

  // Load total asset count and category counts
  useEffect(() => {
    if (projectId) {
      loadTotalAssetCount();
      loadCategoryCounts();
    }
  }, [projectId]);

  // Load assets on mount and when category changes
  useEffect(() => {
    if (projectId) {
      loadAssets();
    }
  }, [projectId, selectedCategory, currentCode]);

  // Auto-upload dropped files from page drag-and-drop
  useEffect(() => {
    // Only process if we have new files and haven't processed them yet
    if (droppedFiles && droppedFiles.length > 0 && droppedFiles !== processedFilesRef.current) {
      console.log('📦 Auto-uploading dropped files:', droppedFiles.length);
      processedFilesRef.current = droppedFiles; // Mark as processed
      handleUpload(droppedFiles);
      
      // Notify parent that files have been processed
      if (onFilesProcessed) {
        onFilesProcessed();
      }
    }
  }, [droppedFiles]);

  const loadTotalAssetCount = async () => {
    if (!projectId) return;
    
    try {
      // Fetch all assets (not filtered by category) to get total count
      const allAssets = await assetService.listAssets(projectId, 'all', false);
      setTotalAssetCount(allAssets?.length || 0);
      setTotalUnusedCount(allAssets?.filter(a => a.uses === 0).length || 0);
    } catch (error) {
      console.error('Failed to load total asset count:', error);
      setTotalAssetCount(0);
      setTotalUnusedCount(0);
    }
  };

  const loadCategoryCounts = async () => {
    if (!projectId) return;
    
    try {
      // Fetch all non-archived assets
      const allAssets = await assetService.listAssets(projectId, 'all', false);
      const archivedAssets = await assetService.listAssets(projectId, 'all', true);
      
      // Count by category
      const counts: Record<AssetCategory, number> = {
        all: allAssets?.length || 0,
        images: 0,
        video: 0,
        audio: 0,
        fonts: 0,
        data: 0,
        '3d': 0,
        archive: archivedAssets?.filter(a => a.isArchived).length || 0,
      };
      
      // Count assets by category
      allAssets?.forEach(asset => {
        if (asset.category && counts.hasOwnProperty(asset.category)) {
          counts[asset.category as AssetCategory]++;
        }
      });
      
      setCategoryCounts(counts);
    } catch (error) {
      console.error('Failed to load category counts:', error);
    }
  };

  const loadAssets = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const includeArchived = selectedCategory === 'archive';
      // For archive view, don't filter by category, just show all archived items
      const category = selectedCategory === 'archive' ? 'all' : selectedCategory;
      const fetchedAssets = await assetService.listAssets(
        projectId,
        category,
        includeArchived
      );
      
      // Calculate usage counts from current code
      const usageMap = currentCode ? assetService.calculateUsageFromCode(currentCode) : new Map();
      
      // Update assets with calculated usage counts
      const assetsWithUsage = (fetchedAssets || []).map(asset => ({
        ...asset,
        uses: usageMap.get(asset.id) || 0
      }));
      
      // Preserve any assets that are currently uploading (have uploadProgress)
      setAssets(prev => {
        const uploadingAssets = prev.filter(a => a.uploadProgress !== undefined);
        console.log(`📋 Loading assets: ${uploadingAssets.length} uploading, ${assetsWithUsage.length} fetched`);
        return [...uploadingAssets, ...assetsWithUsage];
      });
    } catch (error) {
      console.error('Failed to load assets:', error);
      // Preserve uploading assets even on error
      setAssets(prev => prev.filter(a => a.uploadProgress !== undefined));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      handleUpload(Array.from(files));
    }
  };

  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        alert(`${file.name} is too large (max 50MB)`);
        continue;
      }

      // Create temporary asset with progress
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const tempAsset: Asset = {
        id: tempId,
        filename: file.name,
        type: file.type,
        size: formatFileSize(file.size),
        uses: 0,
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        uploadProgress: 0,
      };

      console.log(`📤 Creating temp asset for upload: ${file.name} (${tempId})`);
      setAssets(prev => [tempAsset, ...prev]);

      try {
        // Upload with progress tracking
        const uploadedAsset = await assetService.uploadAsset(
          file,
          projectId,
          (progress) => {
            setAssets(prev =>
              prev.map(a => a.id === tempId ? { ...a, uploadProgress: progress } : a)
            );
          }
        );

        // Track asset upload
        analyticsService.track({
          name: 'asset_uploaded',
          properties: {
            asset_id: uploadedAsset.id,
            project_id: projectId,
            filename: uploadedAsset.filename,
            file_type: uploadedAsset.type,
            file_size_bytes: uploadedAsset.size,
            category: uploadedAsset.category || 'all',
            upload_method: 'file_picker'
          }
        });
        
        // Check if the uploaded asset's category matches the current category
        const assetCategory = uploadedAsset.category || 'all';
        
        if (selectedCategory !== 'all' && assetCategory !== selectedCategory) {
          // Remove temp asset from current view and switch to the correct category
          console.log(`📁 Switching from ${selectedCategory} to ${assetCategory} for uploaded file`);
          setAssets(prev => prev.filter(a => a.id !== tempId));
          setSelectedCategory(assetCategory as AssetCategory);
        } else {
          // Replace temp asset with real asset if we're in the right category or "all"
          setAssets(prev => prev.map(a => a.id === tempId ? uploadedAsset : a));
        }
        
        // Increment total asset count and unused count (new uploads have 0 uses)
        setTotalAssetCount(prev => prev + 1);
        setTotalUnusedCount(prev => prev + 1);
        
        // Update category counts
        setCategoryCounts(prev => ({
          ...prev,
          all: prev.all + 1,
          [assetCategory]: (prev[assetCategory as AssetCategory] || 0) + 1,
        }));
      } catch (error) {
        console.error('Upload failed:', error);
        alert(`Failed to upload ${file.name}`);
        setAssets(prev => prev.filter(a => a.id !== tempId));
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    
    // Mark the event as handled by AssetBrowser
    (event.nativeEvent as any).handledByAssetBrowser = true;
    
    const files = Array.from(event.dataTransfer.files);
    handleUpload(files);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent event from bubbling to parent
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.stopPropagation(); // Prevent event from bubbling to parent
    setIsDragging(false);
  };

  const handleToggleAsset = (assetId: string) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets.map(a => a.id));
    }
  };

  const handleArchiveSelected = async () => {
    try {
      // Check if we're restoring from archive or archiving
      const isRestoring = selectedCategory === 'archive';
      
      if (isRestoring) {
        // Restoring from archive
        const assetsToRestore = assets.filter(a => selectedAssets.includes(a.id));
        const restoreCount = assetsToRestore.length;
        const unusedRestoreCount = assetsToRestore.filter(a => a.uses === 0).length;
        
        await assetService.restoreAssets(selectedAssets);
        setSelectedAssets([]);
        await loadAssets();
        
        // Increment total asset count and unused count
        setTotalAssetCount(prev => prev + restoreCount);
        setTotalUnusedCount(prev => prev + unusedRestoreCount);
      } else {
        // Archiving assets
        const assetsToArchive = assets.filter(a => selectedAssets.includes(a.id));
        const archiveCount = assetsToArchive.length;
        const unusedArchiveCount = assetsToArchive.filter(a => a.uses === 0).length;
        
        await assetService.archiveAssets(selectedAssets);
        setSelectedAssets([]);
        await loadAssets();
        
        // Decrement total asset count and unused count
        setTotalAssetCount(prev => Math.max(0, prev - archiveCount));
        setTotalUnusedCount(prev => Math.max(0, prev - unusedArchiveCount));
      }
      
      // Update category counts
      await loadCategoryCounts();
    } catch (error) {
      console.error(`Failed to ${selectedCategory === 'archive' ? 'restore' : 'archive'} assets:`, error);
      alert(`Failed to ${selectedCategory === 'archive' ? 'restore' : 'archive'} assets`);
    }
  };

  const handleRename = async (assetId: string, currentName: string) => {
    const newName = prompt('Enter new filename:', currentName);
    if (newName && newName !== currentName) {
      try {
        const asset = assets.find(a => a.id === assetId);
        await assetService.renameAsset(assetId, newName);
        
        // Track asset renamed
        analyticsService.track({
          name: 'asset_renamed',
          properties: {
            asset_id: assetId,
            project_id: projectId,
            old_filename: currentName,
            new_filename: newName,
            file_type: asset?.type || 'unknown'
          }
        });
        
        await loadAssets();
      } catch (error) {
        console.error('Failed to rename asset:', error);
        alert('Failed to rename asset');
      }
    }
  };

  const handleArchive = async (assetId: string) => {
    try {
      // Check if asset is unused before archiving
      const asset = assets.find(a => a.id === assetId);
      const isUnused = asset?.uses === 0;
      
      await assetService.archiveAssets([assetId]);
      await loadAssets();
      
      // Decrement total asset count and unused count if applicable
      setTotalAssetCount(prev => Math.max(0, prev - 1));
      if (isUnused) {
        setTotalUnusedCount(prev => Math.max(0, prev - 1));
      }
      
      // Update category counts
      await loadCategoryCounts();
    } catch (error) {
      console.error('Failed to archive asset:', error);
      alert('Failed to archive asset');
    }
  };

  const handleRestore = async (assetId: string) => {
    try {
      // Check if asset is unused before restoring
      const asset = assets.find(a => a.id === assetId);
      const isUnused = asset?.uses === 0;
      
      await assetService.restoreAssets([assetId]);
      await loadAssets();
      
      // Increment total asset count and unused count if applicable
      setTotalAssetCount(prev => prev + 1);
      if (isUnused) {
        setTotalUnusedCount(prev => prev + 1);
      }
      
      // Update category counts
      await loadCategoryCounts();
    } catch (error) {
      console.error('Failed to restore asset:', error);
      alert('Failed to restore asset');
    }
  };

  const handleDelete = async (assetId: string) => {
    try {
      await assetService.deleteAsset(assetId);
      
      // Track asset deleted
      analyticsService.track({
        name: 'asset_deleted',
        properties: {
          asset_ids: [assetId],
          project_id: projectId,
          asset_count: 1,
          deletion_type: 'single'
        }
      });
      
      await loadAssets();
      
      // Update category counts
      await loadCategoryCounts();
      
      // Close the confirmation modal
      setDeleteConfirmAsset(null);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset');
    }
  };

  const handleDeleteSelected = async () => {
    const assetsToDelete = assets.filter(a => selectedAssets.includes(a.id));
    setDeleteConfirmAssets(assetsToDelete);
  };

  const handleDeleteMultiple = async (assetIds: string[]) => {
    try {
      await assetService.deleteAssets(assetIds);
      
      // Track asset deleted
      analyticsService.track({
        name: 'asset_deleted',
        properties: {
          asset_ids: assetIds,
          project_id: projectId,
          asset_count: assetIds.length,
          deletion_type: 'bulk'
        }
      });
      
      await loadAssets();
      
      // Update category counts
      await loadCategoryCounts();
      
      // Clear selection and close modal
      setSelectedAssets([]);
      setDeleteConfirmAssets([]);
    } catch (error) {
      console.error('Failed to delete assets:', error);
      alert('Failed to delete assets');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}b`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}kb`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}mb`;
  };

const getDisplayName = (filename: string): string => {
  // Remove file extension for display
  return filename.replace(/\.[^/.]+$/, '');
};

const getDisplayType = (mimeType: string, filename: string): string => {
  // For generic MIME types, show the file extension instead
  if (mimeType === 'application/octet-stream' || mimeType.includes('octet-stream')) {
    const ext = filename.split('.').pop()?.toUpperCase();
    return ext ? `${ext} File` : mimeType;
  }
  
  // For other generic types, extract extension
  if (mimeType.startsWith('application/') && !mimeType.includes('json') && !mimeType.includes('xml')) {
    const ext = filename.split('.').pop()?.toUpperCase();
    if (ext) return `${ext} File`;
  }
  
  // For known types, show friendly names
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  if (mimeType.includes('font')) return 'Font';
  if (mimeType.includes('json')) return 'JSON';
  if (mimeType.includes('xml')) return 'XML';
  
  return mimeType;
};

  const formatMetadata = (asset: Asset): string | null => {
    if (!asset.metadata) return null;
    
    const meta = asset.metadata;
    
    // Image metadata
    if (meta.type === 'image' && meta.image) {
      return `${meta.image.width}×${meta.image.height}`;
    }
    
    // Audio metadata
    if (meta.type === 'audio' && meta.audio) {
      const parts: string[] = [];
      if (meta.audio.duration) {
        const mins = Math.floor(meta.audio.duration / 60);
        const secs = Math.floor(meta.audio.duration % 60);
        parts.push(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
      if (meta.audio.channelMode) {
        parts.push(meta.audio.channelMode);
      }
      if (meta.audio.bitrate) {
        parts.push(`${Math.round(meta.audio.bitrate / 1000)}kbps`);
      }
      return parts.join(' • ');
    }
    
    // Video metadata
    if (meta.type === 'video' && meta.video) {
      const parts: string[] = [];
      if (meta.video.width && meta.video.height) {
        parts.push(`${meta.video.width}×${meta.video.height}`);
      }
      if (meta.video.duration) {
        const mins = Math.floor(meta.video.duration / 60);
        const secs = Math.floor(meta.video.duration % 60);
        parts.push(`${mins}:${secs.toString().padStart(2, '0')}`);
      }
      if (meta.video.fps) {
        parts.push(`${meta.video.fps}fps`);
      }
      return parts.join(' • ');
    }
    
    // Font metadata
    if (meta.type === 'font' && meta.font) {
      const parts: string[] = [];
      if (meta.font.family) {
        parts.push(meta.font.family);
      }
      if (meta.font.weight || meta.font.style) {
        parts.push(meta.font.weight || meta.font.style || '');
      }
      if (meta.font.glyphCount) {
        parts.push(`${meta.font.glyphCount} glyphs`);
      }
      return parts.join(' • ');
    }
    
    // 3D model metadata
    if (meta.type === '3d' && meta.model) {
      const parts: string[] = [];
      if (meta.model.format) {
        parts.push(meta.model.format);
      }
      if (meta.model.vertices) {
        parts.push(`${meta.model.vertices.toLocaleString()} verts`);
      }
      if (meta.model.faces) {
        parts.push(`${meta.model.faces.toLocaleString()} faces`);
      }
      return parts.join(' • ');
    }
    
    return null;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  return (
    <div className="h-full flex bg-gray-50 dark:bg-gray-900 relative">
      {/* Left Sidebar - Categories */}
      <div className="w-56 bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col flex-shrink-0">
        {/* Asset Stats */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="text-gray-900 dark:text-white text-sm font-semibold">{totalAssetCount} Assets</div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">{totalUnusedCount} Unused</div>
        </div>

        {/* Categories List */}
        <div className="flex-1 overflow-y-auto py-2">
          {/* All Category */}
          <button
            onClick={() => setSelectedCategory('all')}
            className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
              selectedCategory === 'all'
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50'
            }`}
          >
            {getCategoryIcon('all')}
            <span>All</span>
          </button>

          {/* Divider */}
          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

          {/* Middle Categories */}
          {CATEGORIES.slice(1, -1).map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                selectedCategory === category.id
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50'
              }`}
            >
              {getCategoryIcon(category.id)}
              <span className="flex-1">{category.label}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{categoryCounts[category.id]}</span>
            </button>
          ))}

          {/* Divider */}
          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />

          {/* Archive Category */}
          <button
            onClick={() => setSelectedCategory('archive')}
            className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
              selectedCategory === 'archive'
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50'
            }`}
          >
            {getCategoryIcon('archive')}
            <span>Archive</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="flex items-center gap-3 p-4 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Upload Button */}
          <button 
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span className="text-sm font-medium">Upload</span>
          </button>

          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Grid View"
            >
              <Grid3x3 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Selection Banner */}
        {isSelectMode && (
          <div className="flex items-center gap-4 px-4 py-3 bg-primary-600">
            <button
              onClick={() => setSelectedAssets([])}
              className="p-1 hover:bg-primary-700 rounded transition-colors"
              title="Clear selection"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            <div className="text-white font-medium">
              {selectedAssets.length} {selectedAssets.length === 1 ? 'item' : 'items'} selected
            </div>

            <button
              onClick={handleSelectAll}
              className="px-3 py-1.5 bg-primary-700 hover:bg-primary-800 text-white text-sm rounded-lg transition-colors"
            >
              {selectedAssets.length === assets.length ? 'Deselect All' : 'Select All'}
            </button>

            <div className="flex-1" />

            <button
              onClick={handleArchiveSelected}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary-700 hover:bg-primary-800 text-white text-sm rounded-lg transition-colors"
            >
              <ArchiveIcon className="w-4 h-4" />
              <span>{selectedCategory === 'archive' ? 'Restore' : 'Archive'}</span>
            </button>

            {selectedCategory === 'archive' && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            )}
          </div>
        )}

        {/* Assets List/Grid */}
        <div 
          className="flex-1 overflow-y-auto"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {loading && assets.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-600 dark:text-gray-400">Loading assets...</div>
            </div>
          ) : viewMode === 'list' ? (
            <div className="p-4 space-y-2">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => isSelectMode && handleToggleAsset(asset.id)}
                  onMouseEnter={() => setHoveredAssetId(asset.id)}
                  onMouseLeave={() => setHoveredAssetId(null)}
                  className={`flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors relative ${
                    isSelectMode ? 'cursor-pointer' : ''
                  } ${selectedAssets.includes(asset.id) ? 'ring-2 ring-primary-500' : ''}`}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedAssets.includes(asset.id)}
                    onChange={() => handleToggleAsset(asset.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-primary-500 focus:ring-primary-500"
                  />

                  {/* Thumbnail */}
                  <div 
                    className={`w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0 relative overflow-hidden ${
                      asset.type.startsWith('image/') && !isSelectMode ? 'cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all' : ''
                    }`}
                    onClick={(e) => {
                      if (asset.type.startsWith('image/') && !isSelectMode) {
                        e.stopPropagation();
                        setPreviewImage(asset);
                      }
                    }}
                  >
                    {getFileTypeIcon(asset.type, 24, asset.filename)}
                    {asset.type.startsWith('image/') && asset.url && (
                      <img 
                        src={asset.url} 
                        alt={asset.filename}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    {asset.uploadProgress !== undefined && (
                      <UploadProgressOverlay progress={asset.uploadProgress} size="small" />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-900 dark:text-white text-sm font-medium truncate">
                      {getDisplayName(asset.filename)}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400 text-xs">
                      {getDisplayType(asset.type, asset.filename)}
                    </div>
                    <div className="text-gray-500 dark:text-gray-500 text-xs mt-0.5">
                      {formatMetadata(asset) || asset.date}
                    </div>
                  </div>

                  {/* File Size */}
                  <div className="text-gray-600 dark:text-gray-400 text-sm flex-shrink-0">
                    {asset.size}
                  </div>

                  {/* Usage Count */}
                  <div className="flex-shrink-0 w-24 text-right">
                    {asset.uses === 0 ? (
                      <div className="flex items-center justify-end gap-1 text-yellow-500 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span>No Uses</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1.5 text-gray-700 dark:text-gray-300 text-sm">
                        <Link className="w-3.5 h-3.5" />
                        <span>{asset.uses} {asset.uses === 1 ? 'Use' : 'Uses'}</span>
                      </div>
                    )}
                  </div>

                  {/* Add to Prompt Button - Shows on hover in list view */}
                  {onAddToPrompt && hoveredAssetId === asset.id && !isSelectMode && !asset.isArchived && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToPrompt(asset.id, asset.filename);
                      }}
                      style={{ background: FILL_GRADIENT }}
                      className="flex-shrink-0 px-3 py-1.5 text-white text-sm font-medium rounded transition-all hover:opacity-90 hover:scale-[1.02]"
                    >
                      Add to prompt
                    </button>
                  )}

                  {/* More Menu */}
                  <div className="relative flex-shrink-0" ref={openDropdownId === asset.id ? dropdownRef : null}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(openDropdownId === asset.id ? null : asset.id);
                      }}
                      className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {openDropdownId === asset.id && (
                      <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-300 dark:border-gray-600 z-50 min-w-[140px]">
                        {!asset.isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRename(asset.id, asset.filename);
                              setOpenDropdownId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white text-sm"
                          >
                            <Edit3 className="w-4 h-4" />
                            <span>Rename</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (asset.isArchived) {
                              handleRestore(asset.id);
                            } else {
                              handleArchive(asset.id);
                            }
                            setOpenDropdownId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white text-sm"
                        >
                          <ArchiveIcon className="w-4 h-4" />
                          <span>{asset.isArchived ? 'Restore' : 'Archive'}</span>
                        </button>
                        {asset.isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmAsset(asset);
                              setOpenDropdownId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400 text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Drag and Drop Zone - As last list item */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`flex items-center gap-3 p-8 bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                  isDragging ? 'border-primary-500 bg-primary-100 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex-1 flex items-center justify-center gap-3">
                  <Upload className="w-6 h-6 text-gray-500 dark:text-gray-500" />
                  <div className="text-gray-600 dark:text-gray-500">Drag here to upload or click to browse</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 grid grid-cols-4 gap-4">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => isSelectMode && handleToggleAsset(asset.id)}
                  onMouseEnter={() => setHoveredAssetId(asset.id)}
                  onMouseLeave={() => setHoveredAssetId(null)}
                  className={`bg-white dark:bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors relative ${
                    isSelectMode ? 'cursor-pointer' : ''
                  } ${selectedAssets.includes(asset.id) ? 'ring-2 ring-primary-500' : ''}`}
                >
                  {/* Checkbox - Top Left */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedAssets.includes(asset.id)}
                      onChange={() => handleToggleAsset(asset.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-900/80 text-primary-500 focus:ring-primary-500"
                    />
                  </div>

                  {/* More Menu - Top Right */}
                  <div className="absolute top-2 right-2 z-10" ref={openDropdownId === asset.id ? dropdownRef : null}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(openDropdownId === asset.id ? null : asset.id);
                      }}
                      className="p-1.5 bg-white/80 dark:bg-gray-900/80 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-700 dark:text-gray-400" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {openDropdownId === asset.id && (
                      <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-300 dark:border-gray-600 z-50 min-w-[140px]">
                        {!asset.isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRename(asset.id, asset.filename);
                              setOpenDropdownId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white text-sm"
                          >
                            <Edit3 className="w-4 h-4" />
                            <span>Rename</span>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (asset.isArchived) {
                              handleRestore(asset.id);
                            } else {
                              handleArchive(asset.id);
                            }
                            setOpenDropdownId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white text-sm"
                        >
                          <ArchiveIcon className="w-4 h-4" />
                          <span>{asset.isArchived ? 'Restore' : 'Archive'}</span>
                        </button>
                        {asset.isArchived && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmAsset(asset);
                              setOpenDropdownId(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400 text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Thumbnail */}
                  <div 
                    className={`aspect-square bg-gray-200 dark:bg-gray-700 flex items-center justify-center relative overflow-hidden ${
                      asset.type.startsWith('image/') && !isSelectMode ? 'cursor-pointer' : ''
                    }`}
                    onClick={(e) => {
                      if (asset.type.startsWith('image/') && !isSelectMode) {
                        e.stopPropagation();
                        setPreviewImage(asset);
                      }
                    }}
                  >
                    {getFileTypeIcon(asset.type, 64, asset.filename)}
                    {asset.type.startsWith('image/') && asset.url && (
                      <img 
                        src={asset.url} 
                        alt={asset.filename}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    {asset.uploadProgress !== undefined && (
                      <UploadProgressOverlay progress={asset.uploadProgress} />
                    )}
                    
                    {/* Add to Prompt Button - Shows on hover */}
                    {onAddToPrompt && hoveredAssetId === asset.id && !isSelectMode && !asset.isArchived && (
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddToPrompt(asset.id, asset.filename);
                          }}
                          style={{ background: FILL_GRADIENT }}
                          className="w-full py-1.5 px-3 text-white text-sm font-medium rounded transition-all hover:opacity-90 hover:scale-[1.02]"
                        >
                          Add to prompt
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <div className="text-gray-900 dark:text-white text-sm font-medium truncate mb-1">
                      {getDisplayName(asset.filename)}
                    </div>
                    {formatMetadata(asset) && (
                      <div className="text-gray-500 dark:text-gray-500 text-xs mb-1 truncate">
                        {formatMetadata(asset)}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">{asset.size}</span>
                      {asset.uses === 0 ? (
                        <div className="flex items-center gap-1 text-yellow-500">
                          <AlertTriangle className="w-3 h-3" />
                          <span>0</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                          <Link className="w-3 h-3" />
                          <span>{asset.uses}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Drag and Drop Zone - As last grid item */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border-2 border-dashed transition-colors cursor-pointer ${
                  isDragging ? 'border-primary-500 bg-primary-100 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                }`}
              >
                <div className="aspect-square flex flex-col items-center justify-center">
                  <Upload className="w-12 h-12 text-gray-500 dark:text-gray-500 mb-2" />
                  <div className="text-gray-600 dark:text-gray-500 text-sm text-center px-4">
                    Drag or click to upload
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
            >
              <X size={24} />
            </button>

            {/* Image filename */}
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <div className="inline-block px-4 py-2 bg-black/70 rounded-lg text-white text-sm">
                {previewImage.filename}
              </div>
            </div>

            {/* Image */}
            <img 
              src={previewImage.url} 
              alt={previewImage.filename}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Single Asset */}
      {deleteConfirmAsset && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirmAsset(null)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Permanently Delete Asset?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  This action cannot be undone. The asset will be permanently removed from:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 mb-3 ml-4 space-y-1">
                  <li>• Your project</li>
                  <li>• All published versions</li>
                  <li>• Cloud storage</li>
                </ul>
                <div className="bg-gray-100 dark:bg-gray-700/50 rounded px-3 py-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                    {deleteConfirmAsset.filename}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmAsset(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmAsset.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Multiple Assets */}
      {deleteConfirmAssets.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirmAssets([])}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Permanently Delete {deleteConfirmAssets.length} {deleteConfirmAssets.length === 1 ? 'Asset' : 'Assets'}?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  This action cannot be undone. {deleteConfirmAssets.length === 1 ? 'This asset' : 'These assets'} will be permanently removed from:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 mb-3 ml-4 space-y-1">
                  <li>• Your project</li>
                  <li>• All published versions</li>
                  <li>• Cloud storage</li>
                </ul>
                <div className="bg-gray-100 dark:bg-gray-700/50 rounded px-3 py-2 max-h-32 overflow-y-auto">
                  {deleteConfirmAssets.map((asset, index) => (
                    <p key={asset.id} className="text-sm text-gray-900 dark:text-white break-all">
                      {index + 1}. {asset.filename}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmAssets([])}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteMultiple(deleteConfirmAssets.map(a => a.id))}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
              >
                Delete {deleteConfirmAssets.length === 1 ? 'Asset' : 'All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

